import { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Button, Modal, message, Input } from 'antd';
import { 
  DashboardOutlined, 
  CommentOutlined,
  UserOutlined, 
  SafetyCertificateOutlined,
  LogoutOutlined,
  PlusOutlined,
  MessageOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { AdminUser } from '@/types/auth';
import queryService from '@/services/queryService';
import dashboardService from '@/services/dashboardService';
import { Dashboard } from '@/types/dashboard';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  adminUser: AdminUser | null;
  onLogout: () => void;
}

interface QuerySession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

const Sidebar = ({ collapsed, adminUser, onLogout }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState<QuerySession[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>(
    location.pathname.startsWith('/dashboard')
      ? ['dashboard-group']
      : location.pathname === '/query' || location.pathname.startsWith('/query/')
      ? ['query-group']
      : []
  );

  useEffect(() => {
    loadSessions();
    loadDashboards();
    
    // Refresh sessions every 30 seconds when on query page
    const interval = setInterval(() => {
      if (location.pathname.startsWith('/query')) {
        loadSessions();
      }
      if (location.pathname.startsWith('/dashboard')) {
        loadDashboards();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [location.pathname]);

  const loadSessions = async () => {
    try {
      const response = await queryService.getSessions();
      if (response.success && response.sessions) {
        const sortedSessions = response.sessions
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .slice(0, 10);
        setSessions(sortedSessions);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadDashboards = async () => {
    try {
      const response = await dashboardService.getDashboards();
      if (response.success && response.dashboards) {
        const sortedDashboards = response.dashboards
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .slice(0, 10);
        setDashboards(sortedDashboards);
      }
    } catch (error) {
      console.error('Failed to load dashboards:', error);
    }
  };

  const handleRenameSession = (sessionId: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the session load
    
    let newTitle = currentTitle;
    
    const modal = Modal.confirm({
      title: 'Rename Conversation',
      content: (
        <Input
          ref={(ref) => {
            if (ref) {
              setTimeout(() => ref.focus(), 100);
            }
          }}
          defaultValue={currentTitle}
          placeholder="Enter new title"
          onChange={(e) => { newTitle = e.target.value; }}
          onPressEnter={() => {
            modal.destroy();
            performRename();
          }}
        />
      ),
      okText: 'Rename',
      cancelText: 'Cancel',
      onOk: performRename
    });

    async function performRename() {
      if (!newTitle || newTitle.trim() === '' || newTitle === currentTitle) {
        return;
      }
      
      try {
        await queryService.updateSession(sessionId, newTitle.trim());
        message.success('Conversation renamed');
        
        // Reload sessions
        loadSessions();
      } catch (error) {
        console.error('Failed to rename session:', error);
        message.error('Failed to rename conversation');
      }
    }
  };

  const handleDeleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the session load
    
    Modal.confirm({
      title: 'Delete Conversation',
      content: 'Are you sure you want to delete this conversation? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await queryService.deleteSession(sessionId);
          message.success('Conversation deleted');
          loadSessions();

          // If this was the active session, navigate to a new chat
          if (location.pathname === `/query/${sessionId}`) {
            navigate('/query');
          }
        } catch (error) {
          console.error('Failed to delete session:', error);
          message.error('Failed to delete conversation');
        }
      }
    });
  };

  const handleNewChat = () => {
    navigate('/query');
  };

  const handleLoadSession = (sessionId: string) => {
    navigate(`/query/${sessionId}`);
  };

  // Dashboard handlers
  const handleNewDashboard = async () => {
    let dashboardTitle = '';
    
    const modal = Modal.confirm({
      title: 'Create New Dashboard',
      content: (
        <Input
          ref={(ref) => {
            if (ref) {
              setTimeout(() => ref.focus(), 100);
            }
          }}
          placeholder="Enter dashboard title"
          onChange={(e) => { dashboardTitle = e.target.value; }}
          onPressEnter={() => {
            modal.destroy();
            performCreate();
          }}
        />
      ),
      okText: 'Create',
      cancelText: 'Cancel',
      onOk: performCreate
    });

    async function performCreate() {
      if (!dashboardTitle || dashboardTitle.trim() === '') {
        message.error('Please enter a dashboard title');
        return;
      }
      
      try {
        const response = await dashboardService.createDashboard(dashboardTitle.trim());
        if (response.success && response.dashboard) {
          message.success('Dashboard created');
          loadDashboards();
          // Navigate to the new dashboard
          navigate(`/dashboard/${response.dashboard.id}`);
        }
      } catch (error) {
        console.error('Failed to create dashboard:', error);
        message.error('Failed to create dashboard');
      }
    }
  };

  const handleRenameDashboard = (dashboardId: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    let newTitle = currentTitle;
    
    const modal = Modal.confirm({
      title: 'Rename Dashboard',
      content: (
        <Input
          ref={(ref) => {
            if (ref) {
              setTimeout(() => ref.focus(), 100);
            }
          }}
          defaultValue={currentTitle}
          placeholder="Enter new title"
          onChange={(e) => { newTitle = e.target.value; }}
          onPressEnter={() => {
            modal.destroy();
            performRename();
          }}
        />
      ),
      okText: 'Rename',
      cancelText: 'Cancel',
      onOk: performRename
    });

    async function performRename() {
      if (!newTitle || newTitle.trim() === '' || newTitle === currentTitle) {
        return;
      }
      
      try {
        await dashboardService.updateDashboard(dashboardId, newTitle.trim());
        message.success('Dashboard renamed');
        loadDashboards();
      } catch (error) {
        console.error('Failed to rename dashboard:', error);
        message.error('Failed to rename dashboard');
      }
    }
  };

  const handleDeleteDashboard = async (dashboardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    Modal.confirm({
      title: 'Delete Dashboard',
      content: 'Are you sure you want to delete this dashboard? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dashboardService.deleteDashboard(dashboardId);
          message.success('Dashboard deleted');
          loadDashboards();
          
          // If this was the active dashboard, navigate away
          if (location.pathname === `/dashboard/${dashboardId}`) {
            navigate('/dashboard');
            window.dispatchEvent(new CustomEvent('new-dashboard'));
          }
        } catch (error) {
          console.error('Failed to delete dashboard:', error);
          message.error('Failed to delete dashboard');
        }
      }
    });
  };

  const handleLoadDashboard = (dashboardId: string) => {
    navigate(`/dashboard/${dashboardId}`);
    window.dispatchEvent(new CustomEvent('load-dashboard', { detail: { dashboardId } }));
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'dashboard-group',
      icon: <DashboardOutlined />,
      label: 'Dashboards',
      children: [
        {
          key: 'new-dashboard',
          icon: <PlusOutlined />,
          label: 'New Dashboard',
          onClick: handleNewDashboard,
        },
        ...(dashboards.length > 0 ? [{ type: 'divider' as const }] : []),
        ...dashboards.map((dashboard) => {
          return {
            key: `dashboard-${dashboard.id}`,
            icon: dashboard.is_public ? <GlobalOutlined /> : <LockOutlined />,
            label: (
              <div className="flex items-center justify-between group">
                <div className="truncate flex-1 mr-2" title={dashboard.title}>
                  {dashboard.title}
                  {!dashboard.is_owner && dashboard.creator && (
                    <div className="text-xs text-gray-400 truncate">
                      by {dashboard.creator.display_name || dashboard.creator.email}
                    </div>
                  )}
                </div>
                {dashboard.is_owner && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => handleRenameDashboard(dashboard.id, dashboard.title, e)}
                    />
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => handleDeleteDashboard(dashboard.id, e)}
                    />
                  </div>
                )}
              </div>
            ),
            onClick: () => handleLoadDashboard(dashboard.id),
          };
        }),
      ],
    },
    {
      key: 'query-group',
      icon: <CommentOutlined />,
      label: 'Chat',
      children: [
        {
          key: 'new-chat',
          icon: <PlusOutlined />,
          label: 'New Chat',
          onClick: handleNewChat,
        },
        ...(sessions.length > 0 ? [{ type: 'divider' as const }] : []),
        ...sessions.map((session) => {
          return {
            key: `session-${session.id}`,
            icon: <MessageOutlined />,
            label: (
              <div className="flex items-center justify-between group">
                <div className="truncate flex-1 mr-2" title={session.title}>
                  {session.title}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => handleRenameSession(session.id, session.title, e)}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => handleDeleteSession(session.id, e)}
                  />
                </div>
              </div>
            ),
            onClick: () => handleLoadSession(session.id),
          };
        }),
      ],
    },
  ];

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: onLogout,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // Only navigate if it's a direct route (not a session or new-chat which have their own handlers)
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  // Get selected keys - highlight active dashboard or query session
  const getSelectedKeys = () => {
    if (location.pathname.startsWith('/dashboard/')) {
      const dashboardId = location.pathname.split('/dashboard/')[1];
      if (dashboardId) {
        return [`dashboard-${dashboardId}`];
      }
    }
    if (location.pathname === '/dashboard') {
      return ['dashboard-group'];
    }
    if (location.pathname.startsWith('/query/')) {
      const sessionId = location.pathname.split('/query/')[1];
      if (sessionId) {
        return [`session-${sessionId}`];
      }
    }
    if (location.pathname === '/query') {
      return ['/query'];
    }
    return [location.pathname];
  };

  return (
    <Sider 
      collapsible 
      collapsed={collapsed}
      width={256}
      collapsedWidth={80}
      trigger={null}
      className="!overflow-hidden"
      style={{
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        background: '#fff',
        borderRight: '1px solid #f0f0f0',
      }}
    >
      <div className="h-full flex flex-col">
        <div className="h-16 flex items-center justify-center text-lg font-semibold px-4 border-b border-gray-200 gap-3 text-gray-800 shrink-0">
          <SafetyCertificateOutlined className={`${collapsed ? 'text-2xl' : 'text-3xl'} text-primary-500`} />
          {!collapsed && <span>Beemi Admin</span>}
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={getSelectedKeys()}
            openKeys={openKeys}
            onOpenChange={(keys) => {
              // Accordion: only allow one group open at a time
              const groups = ['dashboard-group', 'query-group'];
              const lastOpened = keys.find((k) => !openKeys.includes(k) && groups.includes(k));
              if (lastOpened) {
                setOpenKeys([lastOpened]);
              } else {
                setOpenKeys(keys.filter((k) => groups.includes(k)));
              }
            }}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: 'none',
              marginTop: '8px',
            }}
          />
        </div>
        
        {/* Profile Section at Bottom */}
        {adminUser && (
          <div className="border-t border-gray-200 shrink-0">
            <Dropdown menu={{ items: profileMenuItems }} placement="topRight" trigger={['click']}>
              <div className="cursor-pointer hover:bg-gray-50 transition-colors m-3 p-3 rounded-lg">
                {collapsed ? (
                  <div className="flex justify-center">
                    <Avatar 
                      src={adminUser.image_url} 
                      icon={!adminUser.image_url && <UserOutlined />}
                      size={32}
                    />
                  </div>
                ) : (
                  <Space className="w-full">
                    <Avatar 
                      src={adminUser.image_url} 
                      icon={!adminUser.image_url && <UserOutlined />}
                      size={40}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-800 truncate">
                        {adminUser.display_name || adminUser.email.split('@')[0]}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {adminUser.role}
                      </div>
                    </div>
                  </Space>
                )}
              </div>
            </Dropdown>
          </div>
        )}
      </div>
    </Sider>
  );
};

export default Sidebar;
